/**
 * Layout Engine for Flowchart
 * 
 * Calculates optimal positions for task nodes based on their dependencies.
 * Uses a layered/hierarchical layout with crossing minimization.
 * 
 * VERTICAL FLOW: Nodes flow from top to bottom
 * - Layer 0 (no dependencies) at top
 * - Subsequent layers below
 * - Minimizes edge crossings
 * - Maintains consistent gaps between nodes
 * 
 * DISCONNECTED COMPONENTS (CHAINS):
 * - Separate chains (no connections between them) are laid out independently
 * - Each component is positioned side by side with a gap
 * - This prevents one chain's layout from affecting another
 * - Chains can be named and reordered by the user
 */

import { buildDependencyGraph } from './graphBuilder';
import { assignLayers } from './layerAssignment';
import { minimizeCrossings } from './crossingMinimization';
import { positionNodes } from './nodePositioning';
import { HORIZONTAL_GAP, PADDING, getNodeDimensions } from './constants';
import { getNonMilestoneTaskIds, isMilestoneNode, getMilestoneAnchor, applyMilestonePositionsAfterSyncLayout } from '../utils/milestoneUtils';
import { getNoteAnchor, isNoteNode } from '../utils/noteUtils';
import { withLayoutDimensionDefaults } from './layoutDimensions';

// Re-export constants for external use
export { GAP, DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT } from './constants';
export { buildDependencyGraph } from './graphBuilder';
export { detectCycles } from './detectCycles';

// Multi-campaign layout utilities
export { layoutMultiCampaignTasks, getMultiCampaignLayoutWidth } from './multiCampaignLayout';

// Chain-relative positioning (dynamic tetris compaction)
export { calculateChainOffsets, applyChainOffsets, buildChainInfo } from './chainOffsets';

/**
 * Generate a stable chain ID from the tasks in the chain
 * Uses the sorted task IDs to create a deterministic identifier
 */
function generateChainId(tasks) {
  const sortedIds = tasks.map(t => t.id).sort();
  // Use first task ID as the chain ID (stable as long as that task exists)
  return sortedIds[0];
}

/**
 * Find all disconnected components in the task graph
 * Uses Union-Find to group connected tasks
 * @param {Array} tasks - Array of task objects
 * @param {Object} graph - Dependency graph
 * @returns {Array} Array of { id, tasks } objects, each representing a connected component
 */
function findDisconnectedComponents(tasks, graph) {
  if (tasks.length === 0) return [];
  
  // Union-Find data structure
  const parent = new Map();
  const rank = new Map();
  
  // Initialize: each task is its own parent
  tasks.forEach(task => {
    parent.set(task.id, task.id);
    rank.set(task.id, 0);
  });
  
  // Find with path compression
  function find(x) {
    if (parent.get(x) !== x) {
      parent.set(x, find(parent.get(x)));
    }
    return parent.get(x);
  }
  
  // Union by rank
  function union(x, y) {
    const rootX = find(x);
    const rootY = find(y);
    if (rootX === rootY) return;
    
    const rankX = rank.get(rootX);
    const rankY = rank.get(rootY);
    
    if (rankX < rankY) {
      parent.set(rootX, rootY);
    } else if (rankX > rankY) {
      parent.set(rootY, rootX);
    } else {
      parent.set(rootY, rootX);
      rank.set(rootX, rankX + 1);
    }
  }
  
  // Union tasks that are connected (via allParents/allChildren to include back-edges)
  tasks.forEach(task => {
    const parents = graph.allParents.get(task.id) || [];
    const children = graph.allChildren.get(task.id) || [];
    
    parents.forEach(parentId => {
      if (parent.has(parentId)) {
        union(task.id, parentId);
      }
    });
    
    children.forEach(childId => {
      if (parent.has(childId)) {
        union(task.id, childId);
      }
    });
  });

  // Milestones / notes have no mission dependencies in the graph — attach each
  // to its host so ELK / sync layout keeps them in the same chain component.
  tasks.forEach((task) => {
    if (isMilestoneNode(task)) {
      const anchor = getMilestoneAnchor(task);
      if (anchor?.hostTaskId && parent.has(anchor.hostTaskId)) {
        union(task.id, anchor.hostTaskId);
      }
      return;
    }
    if (isNoteNode(task)) {
      const hostTaskId = getNoteAnchor(task)?.hostTaskId;
      if (hostTaskId && parent.has(hostTaskId)) {
        union(task.id, hostTaskId);
      }
    }
  });
  
  // Group tasks by their root
  const components = new Map();
  tasks.forEach(task => {
    const root = find(task.id);
    if (!components.has(root)) {
      components.set(root, []);
    }
    components.get(root).push(task);
  });
  
  // Convert to array with IDs
  const componentsWithIds = Array.from(components.values()).map(componentTasks => ({
    id: generateChainId(componentTasks),
    tasks: componentTasks,
  }));
  
  // Sort components by the minimum task ID in each (for determinism when no stored order)
  componentsWithIds.sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  
  return componentsWithIds;
}

/**
 * Export findDisconnectedComponents for use by other modules
 */
export { findDisconnectedComponents };

/**
 * Calculate the bounding box of a set of positions
 */
function getBoundingBox(positions, nodeDimensions) {
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  
  Object.entries(positions).forEach(([taskId, pos]) => {
    if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
    const dims = getNodeDimensions(taskId, nodeDimensions);
    minX = Math.min(minX, pos.x);
    maxX = Math.max(maxX, pos.x + dims.width);
    minY = Math.min(minY, pos.y);
    maxY = Math.max(maxY, pos.y + dims.height);
  });
  
  // If no valid positions were found, return a safe default
  if (!Number.isFinite(minX)) {
    return { minX: PADDING, maxX: PADDING, minY: PADDING, maxY: PADDING, width: 0, height: 0 };
  }
  
  return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY };
}

/**
 * Layout a single connected component
 */
function layoutComponent(tasks, nodeDimensions) {
  if (tasks.length === 0) return {};
  
  // Build dependency graph for this component
  const graph = buildDependencyGraph(tasks);
  
  // Assign layers based on dependencies
  const layerData = assignLayers(tasks, graph);
  
  // Optimize node order within layers to minimize crossings
  const optimizedLayers = minimizeCrossings(layerData, graph);
  
  // Position nodes within their layers, using actual dimensions
  const positions = positionNodes(optimizedLayers, graph, nodeDimensions);
  
  // Safety net: ensure ALL tasks have positions
  // Some tasks might be missed if the layout engine encounters edge cases
  // (e.g., complex feedback loops where a node falls through the cracks)
  tasks.forEach(task => {
    if (!positions[task.id]) {
      console.warn('[layoutComponent] Task missing position after layout, assigning default:', task.id);
      positions[task.id] = { x: PADDING, y: PADDING };
    }
  });

  applyMilestonePositionsAfterSyncLayout(positions, tasks, nodeDimensions);
  
  return positions;
}

/**
 * Calculate the layout positions for all tasks
 * @param {Array} tasks - Array of task objects
 * @param {Object} nodeDimensions - Optional map of taskId -> { width, height }
 * @param {Array} chainOrder - Optional array of chain IDs specifying the horizontal order
 * @returns {Object} { positions: Map of taskId -> { x, y }, chainInfo: Array of chain metadata }
 * 
 * IMPORTANT: Positions are ABSOLUTE and do NOT depend on container size.
 * This ensures nodes don't move when the window resizes.
 */
export function calculateLayout(tasks, nodeDimensions = {}, chainOrder = null) {
  if (!tasks || tasks.length === 0) {
    return { positions: {}, chainInfo: [] };
  }

  nodeDimensions = withLayoutDimensionDefaults(tasks, nodeDimensions);

  // Build dependency graph (needed for component detection)
  const graph = buildDependencyGraph(tasks);
  
  // Find disconnected components
  const components = findDisconnectedComponents(tasks, graph);
  
  // If only one component, use the standard layout
  if (components.length <= 1) {
    // Assign layers based on dependencies
    const layerData = assignLayers(tasks, graph);
    
    // Optimize node order within layers to minimize crossings
    const optimizedLayers = minimizeCrossings(layerData, graph);
    
    // Position nodes within their layers, using actual dimensions
    const positions = positionNodes(optimizedLayers, graph, nodeDimensions);
    
    // Safety net: ensure ALL tasks have positions
    tasks.forEach(task => {
      if (!positions[task.id]) {
        console.warn('[calculateLayout] Task missing position after layout, assigning default:', task.id);
        positions[task.id] = { x: PADDING, y: PADDING };
      }
    });

    applyMilestonePositionsAfterSyncLayout(positions, tasks, nodeDimensions);
    
    // Single chain info
    const chainInfo = components.length === 1 ? [{
      id: components[0].id,
      taskIds: getNonMilestoneTaskIds(components[0].tasks),
      position: { x: PADDING, y: PADDING },
      width: getBoundingBox(
        Object.fromEntries(
          getNonMilestoneTaskIds(components[0].tasks)
            .filter((id) => positions[id])
            .map((id) => [id, positions[id]]),
        ),
        nodeDimensions,
      ).width,
    }] : [];
    
    return { positions, chainInfo };
  }
  
  // Multiple components: order by stored chainOrder if provided
  let orderedComponents = [...components];
  
  if (chainOrder && chainOrder.length > 0) {
    // Sort components by their position in chainOrder
    // Chains not in chainOrder go to the end (sorted by ID)
    const orderMap = new Map(chainOrder.map((id, idx) => [id, idx]));
    
    orderedComponents.sort((a, b) => {
      const orderA = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
      const orderB = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
      
      if (orderA !== orderB) return orderA - orderB;
      // Both not in order, sort by ID
      return (a.id || '').localeCompare(b.id || '');
    });
  }
  
  // Layout each component independently first
  const componentLayouts = orderedComponents.map(component => {
    const positions = layoutComponent(component.tasks, nodeDimensions);
    const bbox = getBoundingBox(positions, nodeDimensions);
    
    // Normalize to start at (0, PADDING) for easier tetris calculation
    const normalizedPositions = {};
    Object.entries(positions).forEach(([taskId, pos]) => {
      if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
      normalizedPositions[taskId] = {
        x: pos.x - bbox.minX,
        y: pos.y - bbox.minY + PADDING,
      };
    });
    
    return {
      component,
      positions: normalizedPositions,
      bbox: getBoundingBox(normalizedPositions, nodeDimensions),
    };
  });
  
  // Place chains using tetris logic - each chain positioned as far left as possible
  // while avoiding overlap with already-placed chains
  const allPositions = {};
  const chainInfo = [];
  const placedNodes = []; // Track all placed nodes for tetris calculations
  
  componentLayouts.forEach((layout, index) => {
    const { component, positions, bbox } = layout;
    
    // Build node info for tetris calculation
    const chainNodes = [];
    Object.entries(positions).forEach(([taskId, pos]) => {
      const dims = getNodeDimensions(taskId, nodeDimensions);
      chainNodes.push({
        id: taskId,
        x: pos.x,
        y: pos.y,
        width: dims.width,
        height: dims.height,
        right: pos.x + dims.width,
        bottom: pos.y + dims.height,
      });
    });
    
    // Calculate target X using tetris logic
    let targetX = PADDING;
    
    if (placedNodes.length > 0) {
      // For each node in this chain, find the minimum X that avoids overlap
      // The chain's targetX is determined by the most constrained node
      let maxRequiredX = PADDING;
      
      chainNodes.forEach(chainNode => {
        // Find all placed nodes that overlap vertically
        const verticallyOverlapping = placedNodes.filter(placed => {
          return chainNode.y < placed.bottom && chainNode.bottom > placed.y;
        });
        
        if (verticallyOverlapping.length > 0) {
          // Find the rightmost edge of overlapping nodes
          let rightmostEdge = -Infinity;
          verticallyOverlapping.forEach(placed => {
            rightmostEdge = Math.max(rightmostEdge, placed.right);
          });
          
          // This node must be at least HORIZONTAL_GAP from the rightmost edge
          const minAllowedX = rightmostEdge + HORIZONTAL_GAP;
          
          // Calculate what the chain's minX would need to be
          const nodeOffsetFromChainMin = chainNode.x - bbox.minX;
          const chainMinXForThisNode = minAllowedX - nodeOffsetFromChainMin;
          
          maxRequiredX = Math.max(maxRequiredX, chainMinXForThisNode);
        }
      });
      
      targetX = maxRequiredX;
    }
    
    // Apply offset to place chain at targetX
    const offsetX = targetX - bbox.minX;
    
    Object.entries(positions).forEach(([taskId, pos]) => {
      const finalPos = {
        x: pos.x + offsetX,
        y: pos.y,
      };
      allPositions[taskId] = finalPos;
      
      // Add to placed nodes for next iteration
      const dims = getNodeDimensions(taskId, nodeDimensions);
      placedNodes.push({
        id: taskId,
        x: finalPos.x,
        y: finalPos.y,
        width: dims.width,
        height: dims.height,
        right: finalPos.x + dims.width,
        bottom: finalPos.y + dims.height,
      });
    });
    
    // Store chain info for rendering headers
    const finalBbox = getBoundingBox(
      Object.fromEntries(
        getNonMilestoneTaskIds(component.tasks)
          .filter((id) => allPositions[id])
          .map((id) => [id, allPositions[id]]),
      ),
      nodeDimensions,
    );
    
    chainInfo.push({
      id: component.id,
      taskIds: getNonMilestoneTaskIds(component.tasks),
      position: { x: targetX, y: PADDING },
      width: finalBbox.width,
    });
  });
  
  return { positions: allPositions, chainInfo };
}

/**
 * Legacy wrapper for calculateLayout that returns just positions
 * (for backwards compatibility)
 */
export function calculateLayoutPositionsOnly(tasks, nodeDimensions = {}, chainOrder = null) {
  const result = calculateLayout(tasks, nodeDimensions, chainOrder);
  return result.positions || result;
}

